import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useMyFleet, useModuleCatalog, useRenameShip, useRepairShip, useRepairAll, useDefense, type ModuleCatalogDto, type ResourcesDto, type ShipDto } from '@/api/hooks'
import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { useMercureSubscription, type MercureEventData } from '@/api/mercure'
import { useCountdown } from '@/hooks/useCountdown'
import { Shipyard } from '@/components/Shipyard'
import { ShipModuleSlots } from '@/components/ShipModuleSlots'
import { ShipCombatStatsRow, ShipCombatStatsInline, boostedShipStats, forgeBoostTitles } from '@/components/ShipCombatStats'
import { EditableName } from '@/components/EditableName'
import { HullAlert, HullRowChip, hullStripeClass } from '@/components/HullAlert'
import { AnchorIcon, BuildingIcon, ShipIcon, GoldIcon, WoodIcon, ModuleIcon, PennantIcon, ChevronRight, ChevronDown } from '@/components/game/GameIcons'
import { SeaScene } from '@/components/game/SeaScene'
import { groupShipsByType } from '@/lib/fleetGroups'
import { useViewMode, ViewToggle, useCollapse, CollapseAllButton, CollapsibleGroup } from '@/components/game/collapse'

/** Human toast for a failed repair — maps the one domain error worth naming, generic otherwise. */
function repairErrorToast(err: unknown): void {
  if (err instanceof ApiError && err.reason === 'insufficient_wood') {
    toast.error('Pas assez de bois pour réparer')
    return
  }
  toast.error(err instanceof Error ? err.message : 'Réparation impossible')
}

/** Display order for active ships: ready first, then busy, then building. */
const STATUS_ORDER: Record<ShipDto['status'], number> = {
  docked: 0,
  in_mission: 1,
  refitting: 2,
  under_construction: 3,
  damaged: 4,
  lost: 99,
}

const STATUS_COLOR: Record<ShipDto['status'], string> = {
  under_construction: 'text-ocean-300',
  docked: 'text-green-300',
  in_mission: 'text-sand-300',
  refitting: 'text-sand-300',
  damaged: 'text-rum-500',
  lost: 'text-red-500',
}

export function Fleet() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useMyFleet()
  const { data: modules } = useModuleCatalog()
  const [view, setView] = useViewMode('corsalia.fleet.view')
  const collapse = useCollapse()

  const handleMercure = useCallback(
    (event: MercureEventData) => {
      if (event.event === 'ship_constructed') {
        qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
        toast.success('⚓ Nouveau navire à quai !', { duration: 5000 })
      }
    },
    [qc],
  )
  useMercureSubscription(user ? [`/users/${user.id}/ships`] : [], handleMercure, !!user)

  if (isLoading) {
    return <div className="max-w-4xl mx-auto p-6 text-ocean-100">Chargement de la flotte…</div>
  }

  if (isError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-rum-500">Impossible de charger la flotte : {(error as Error).message}</p>
      </div>
    )
  }

  if (!data) return null

  const active = data.ships
    .filter((s) => s.status !== 'lost')
    .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
  const lost = data.ships.filter((s) => s.status === 'lost')
  const groups = active.length > 0 ? groupShipsByType(active) : []
  const groupKeys = groups.map((g) => g.type)

  // Progressive disclosure: the per-ship module slots only surface once at least
  // one module type is unlocked (Forge tech or reputation latch). A brand-new
  // player with no Forge research sees no module UI, exactly as before.
  const catalog = modules?.catalog ?? []
  const hasAnyRecipe = catalog.some((c) => c.recipeUnlocked)

  // Fleet-level repair banner (Direction C) — only docked ships can be repaired;
  // a ship at sea/under refit/under construction never contributes here.
  const damagedDocked = data.ships.filter((s) => s.status === 'docked' && s.repairCostWood > 0)
  const dockedGroups = groupShipsByType(data.ships.filter((s) => s.status === 'docked'))

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-[300px] space-y-6">
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
        <h1 className="font-display text-3xl sm:text-4xl text-sand-300 flex items-center gap-3">
          <AnchorIcon className="w-8 h-8 text-sand-300" /> Ma flotte
        </h1>
        {dockedGroups.length > 0 && (
          <span className="flex items-center gap-2 text-ocean-200">
            <AnchorIcon className="w-4 h-4 text-sand-300/70" />
            <span className="text-[11px] uppercase tracking-wider text-ocean-300">À quai</span>
            {dockedGroups.map((g) => (
              <span
                key={g.type}
                className="inline-flex items-center gap-1 tabular-nums"
                title={`${g.ships.length} ${g.label} à quai`}
              >
                {g.ships.length}
                <ShipIcon type={g.type} className="w-5 h-5 text-sand-300" />
              </span>
            ))}
          </span>
        )}
        {data.berths && (
          <span
            className={`inline-flex items-center gap-1.5 tabular-nums ${
              data.berths.used >= data.berths.total ? 'text-red-400' : 'text-ocean-200'
            }`}
            title={`Mouillages occupés : ${data.berths.used}/${data.berths.total} (la Capitainerie fixe la taille de ta flotte)`}
          >
            <BuildingIcon type="capitainerie" className="w-4 h-4 text-sand-300/70" />
            <span className="text-[11px] uppercase tracking-wider text-ocean-300">Mouillages</span>
            {data.berths.used}/{data.berths.total}
          </span>
        )}
      </div>

      <MobilizedFleetBand />

      {damagedDocked.length > 0 && <FleetRepairBanner ships={damagedDocked} />}

      <section>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="font-display text-2xl text-sand-100">Navires ({active.length})</h2>
          {active.length > 0 && (
            <div className="flex items-center gap-2">
              <CollapseAllButton keys={groupKeys} collapse={collapse} />
              <ViewToggle view={view} onChange={setView} />
            </div>
          )}
        </div>
        {active.length === 0 ? (
          <p className="text-ocean-300 italic">Aucun navire à flot. Direction le chantier.</p>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => (
              <CollapsibleGroup
                key={group.type}
                title={group.label}
                count={group.ships.length}
                icon={<ShipIcon type={group.type} className="w-5 h-5 text-sand-300" />}
                open={collapse.isOpen(group.type)}
                onToggle={() => collapse.toggle(group.type)}
              >
                {view === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {group.ships.map((ship) => (
                      <ShipCard
                        key={ship.id}
                        ship={ship}
                        catalog={catalog}
                        resources={data.resources}
                        showModules={hasAnyRecipe}
                      />
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {group.ships.map((ship) => (
                      <ShipRow key={ship.id} ship={ship} showModules={hasAnyRecipe} />
                    ))}
                  </ul>
                )}
              </CollapsibleGroup>
            ))}
          </div>
        )}
      </section>

      {lost.length > 0 && <LostShips ships={lost} />}

      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-4 text-sm text-ocean-200 flex items-start gap-2.5">
        <GoldIcon className="w-5 h-5 text-sand-300 shrink-0 mt-0.5" />
        <p>
          Pour lancer une expédition, explorer ou raider, file sur la{' '}
          <Link to="/map" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">carte du monde</Link>
          {' '}et clique sur une île.
        </p>
      </div>

      {data.shipyard && data.resources && (
        <Shipyard shipyard={data.shipyard} resources={data.resources} berths={data.berths} />
      )}

      {/* Integrated maritime backdrop — a fleet at sail, always present at the foot of the page. */}
      <SeaScene scene="map" />
    </div>
  )
}

/** Fleet-level alert (Direction C) — only rendered when ≥1 docked ship needs repair. */
function FleetRepairBanner({ ships }: { ships: ShipDto[] }) {
  const repairAll = useRepairAll()
  const totalWood = ships.reduce((acc, s) => acc + s.repairCostWood, 0)

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-red-500/40 bg-red-500/[0.13] px-4 py-3">
      <span className="text-sm font-semibold text-red-300 flex items-center gap-2">
        ⚑ {ships.length} navire{ships.length > 1 ? 's' : ''} à réparer, total <WoodIcon className="w-4 h-4" />{totalWood}
      </span>
      <button
        type="button"
        onClick={() =>
          repairAll.mutate(undefined, {
            onSuccess: () => toast.success('Flotte réparée.'),
            onError: repairErrorToast,
          })
        }
        disabled={repairAll.isPending}
        className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-red-400/60 bg-red-500/15 text-red-100 hover:bg-red-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {repairAll.isPending ? 'Réparation…' : 'Tout réparer'}
      </button>
    </div>
  )
}

function ShipCard({
  ship,
  catalog,
  resources,
  showModules,
}: {
  ship: ShipDto
  catalog: ModuleCatalogDto[]
  resources: ResourcesDto | null
  showModules: boolean
}) {
  const countdown = useCountdown(ship.constructionResolvesAt)
  const missionCountdown = useCountdown(ship.missionResolvesAt)
  const rename = useRenameShip()
  const repair = useRepairShip()

  // Hull chrome + repair only apply to a docked ship — a ship at sea, refitting,
  // under construction, or lost can't be repaired (there's nothing to show).
  const isDocked = ship.status === 'docked'
  const stripe = isDocked ? hullStripeClass(ship) : ''

  return (
    <div className={`rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3 ${stripe}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-ocean-900/50 border border-sand-500/25 shrink-0">
            <ShipIcon type={ship.shipType ?? 'brick'} className="w-6 h-6 text-sand-300" />
          </span>
          <div className="min-w-0">
            <EditableName
              value={ship.name}
              onSave={(name) => rename.mutateAsync({ shipId: ship.id, name })}
              className="font-display text-xl text-sand-300"
              inputClassName="font-display text-xl text-sand-100 w-full"
            />
            <p className="text-[11px] text-ocean-200 uppercase tracking-wider">{ship.shipTypeLabel}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <span className={`text-xs font-semibold ${STATUS_COLOR[ship.status]}`}>{ship.statusLabel}</span>
          {ship.status === 'in_mission' && ship.missionResolvesAt && (
            <p className="text-[11px] text-ocean-300 tabular-nums mt-0.5">⏱ {missionCountdown}</p>
          )}
          <MobilizedBadge ship={ship} />
        </div>
      </div>

      {ship.isUnderConstruction ? (
        <div className="bg-ocean-900/55 rounded-lg p-2 text-center border border-sand-500/10">
          <p className="text-xs text-ocean-300 uppercase tracking-wider">Construction</p>
          <p className="font-display text-lg text-sand-100 tabular-nums">{countdown}</p>
        </div>
      ) : (
        <>
          <ShipCombatStatsRow stats={{ ...ship.combatStats, cargo: ship.cargoCapacity }} boosted={boostedShipStats(ship)} boostTitles={forgeBoostTitles(ship)} />
          {showModules && (
            <ShipModuleSlots
              ship={ship}
              catalog={catalog}
              resources={resources}
              disabled={ship.status !== 'docked'}
            />
          )}
          {isDocked && (
            <HullAlert
              ship={ship}
              repairing={repair.isPending}
              onRepair={() =>
                repair.mutate(ship.id, {
                  onSuccess: () => toast.success(`${ship.name} réparé.`),
                  onError: repairErrorToast,
                })
              }
            />
          )}
        </>
      )}
    </div>
  )
}

/** Compact "details" row — the Windows-Explorer-style list alternative to the card grid. */
/**
 * Récap du branle-bas en tête de page (fiche #27). Lit l'état de défense, qui
 * est déjà en cache dès que le joueur a ouvert son île, donc pas de requête en
 * plus dans le cas courant. Ne rend RIEN quand l'île est au mouillage : la page
 * flotte ne doit pas porter un bandeau permanent pour un état exceptionnel.
 */
function MobilizedFleetBand() {
  const { data } = useDefense()
  const mob = data?.mobilization
  const countdown = useCountdown(mob?.endsAt ?? null)

  if (!mob?.active) return null

  return (
    <div className="rounded-xl border border-amber-400/50 bg-gradient-to-r from-amber-400/12 to-ocean-900/40 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
      <PennantIcon className="w-4 h-4 text-amber-300 shrink-0" aria-hidden="true" />
      <span className="font-display text-base text-amber-300">Branle-bas</span>
      <span className="text-xs text-ocean-300">
        {mob.engaged.length} navire{mob.engaged.length > 1 ? 's' : ''} aux rangs sur {mob.shipsAtQuay} à quai
        {mob.outOfRanks > 0 && `, ${mob.outOfRanks} hors rangs`}
      </span>
      <span className="text-xs text-ocean-300">
        tenable <span className="font-display text-sm text-sand-100 tabular-nums">{countdown}</span>
      </span>
      <span className="flex-1 min-w-2" />
      <Link to="/ile" className="text-xs text-ocean-300 underline decoration-ocean-500/60 underline-offset-2 hover:text-sand-200 transition-colors">
        Gérer depuis la défense
      </Link>
    </div>
  )
}

/**
 * Branle-bas (fiche #27) : l'état de la coque vis-à-vis de la défense de l'île.
 * Trois lectures possibles, et la troisième est celle qui compte pédagogiquement :
 * aux rangs (elle se battra), à l'ancre hors rangs (le plafond du Chantier naval
 * l'a laissée de côté, elle ne se battra PAS), ou rien du tout si l'île est au
 * mouillage. Sans ça le joueur ne comprendrait pas pourquoi 3 de ses 11 navires
 * à quai ne défendent rien.
 */
function MobilizedBadge({ ship }: { ship: ShipDto }) {
  if (ship.mobilized) {
    return (
      <span
        className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold border border-amber-400/40 bg-amber-400/15 text-amber-300"
        title="Aux rangs : ce navire défendra l'île, et ne peut pas partir en mission"
      >
        <PennantIcon className="w-3 h-3" aria-hidden="true" />
        Aux rangs
      </span>
    )
  }

  return null
}

function ShipRow({ ship, showModules }: { ship: ShipDto; showModules: boolean }) {
  const countdown = useCountdown(ship.constructionResolvesAt)
  const missionCountdown = useCountdown(ship.missionResolvesAt)
  const rename = useRenameShip()
  const repair = useRepairShip()

  // Hull chrome + repair only apply to a docked ship (same rule as ShipCard).
  const isDocked = ship.status === 'docked'
  const stripe = isDocked ? hullStripeClass(ship) : ''

  return (
    <li className={`flex items-center gap-3 rounded-xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 px-3 py-2.5 ${stripe}`}>
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-ocean-900/50 border border-sand-500/25 shrink-0">
        <ShipIcon type={ship.shipType ?? 'brick'} className="w-5 h-5 text-sand-300" />
      </span>

      <div className="min-w-0 flex-1">
        <EditableName
          value={ship.name}
          onSave={(name) => rename.mutateAsync({ shipId: ship.id, name })}
          className="font-display text-base text-sand-300"
          inputClassName="font-display text-base text-sand-100 w-full"
        />
        <p className="text-[10px] text-ocean-200 uppercase tracking-wider truncate">{ship.shipTypeLabel}</p>
      </div>

      {ship.isUnderConstruction ? (
        <span className="hidden sm:flex items-center gap-1.5 text-ocean-300">
          <span className="uppercase tracking-wider text-[10px]">Construction</span>
          <span className="font-display text-sand-100 tabular-nums">{countdown}</span>
        </span>
      ) : (
        <>
          {/* Combat stats hide first on a narrow row so the hull chip always has room. */}
          <div className="hidden md:block">
            <ShipCombatStatsInline stats={{ ...ship.combatStats, cargo: ship.cargoCapacity }} boosted={boostedShipStats(ship)} boostTitles={forgeBoostTitles(ship)} />
          </div>
          {showModules && ship.modules.length > 0 && (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-[11px] text-ocean-200 tabular-nums"
              title={ship.modules.map((m) => m.label).join(', ')}
            >
              <ModuleIcon className="w-3.5 h-3.5 text-sand-300" />
              {ship.modules.length}
            </span>
          )}
          {isDocked && (
            <HullRowChip
              ship={ship}
              repairing={repair.isPending}
              onRepair={() =>
                repair.mutate(ship.id, {
                  onSuccess: () => toast.success(`${ship.name} réparé.`),
                  onError: repairErrorToast,
                })
              }
            />
          )}
        </>
      )}

      <span className="shrink-0 text-right whitespace-nowrap">
        <span className={`block text-xs font-semibold ${STATUS_COLOR[ship.status]}`}>{ship.statusLabel}</span>
        {ship.status === 'in_mission' && ship.missionResolvesAt && (
          <span className="block text-[11px] text-ocean-300 tabular-nums">⏱ {missionCountdown}</span>
        )}
        <MobilizedBadge ship={ship} />
      </span>
    </li>
  )
}

function LostShips({ ships }: { ships: ShipDto[] }) {
  const [open, setOpen] = useState(false)

  return (
    <section>
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-sm text-ocean-300 hover:text-ocean-100 transition-colors flex items-center gap-2"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Navires perdus ({ships.length})
      </button>
      {open && (
        <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          {ships.map((s) => (
            <li
              key={s.id}
              className="text-sm text-ocean-400 bg-ocean-900/30 rounded px-3 py-2 border border-sand-500/10 flex items-center justify-between"
            >
              <span className="line-through">{s.name}</span>
              <span className="text-xs text-ocean-500">{s.shipTypeLabel} · coulé</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
